import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, ArrowRight, Bot, Book, User, BarChart3, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Cmd+K Command Palette — Raycast-style quick switcher.
 * Opens with ⌘K / Ctrl+K. Dark glass surface, 2px neon left border for focus.
 */
const CommandPalette = ({
  agents = [],
  onSelectAgent,
  extraActions = [],
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Built-in navigation actions
  const navActions = [
    { id: 'nav-create', label: '對話 Chat', icon: Bot, shortcut: 'G C', run: () => navigate('/create') },
    { id: 'nav-library', label: '案例參考庫', icon: Book, shortcut: 'G L', run: () => navigate('/cases') },
    { id: 'nav-profile', label: '個人中心', icon: User, shortcut: 'G P', run: () => navigate('/profile') },
    { id: 'nav-admin-stats', label: '數據儀表板', icon: BarChart3, shortcut: 'G S', run: () => navigate('/admin/stats') },
    { id: 'nav-admin-settings', label: '系統設定', icon: Settings, shortcut: 'G T', run: () => navigate('/admin/settings') },
    ...extraActions,
  ];

  const agentItems = agents.map((a) => ({
    id: `agent-${a.id}`,
    label: a.name,
    icon: null,
    emoji: a.icon,
    description: a.description,
    run: () => onSelectAgent && onSelectAgent(a),
  }));

  const all = [
    ...(agentItems.length
      ? [{ _group: 'AGENTS', items: agentItems }]
      : []),
    { _group: 'NAVIGATE', items: navActions },
  ];

  const flat = all.flatMap((g) => g.items);
  const filtered = query
    ? flat.filter((i) =>
        (i.label + ' ' + (i.description || '')).toLowerCase().includes(query.toLowerCase())
      )
    : flat;

  // Global ⌘K listener
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setCursor(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    setCursor(0);
  }, [query]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = filtered[cursor];
      if (item) {
        item.run();
        setOpen(false);
      }
    }
  };

  return (
    <>
      {/* Floating hint button (bottom-left) */}
      <button
        onClick={() => setOpen(true)}
        className="hidden md:flex fixed bottom-5 left-5 z-40 items-center gap-2 px-3 py-2 surface-glass-dark rounded-full text-xs text-obsidian-200 hover:text-white hover:shadow-neon transition-all"
        data-testid="cmdk-trigger"
      >
        <Search className="w-3.5 h-3.5" strokeWidth={2} />
        <span className="label-micro">Command</span>
        <kbd className="px-1.5 py-0.5 bg-obsidian-700 rounded text-[10px] font-mono border border-obsidian-600">
          ⌘K
        </kbd>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] flex items-start justify-center pt-[15vh] px-4"
            onClick={() => setOpen(false)}
            data-testid="cmdk-overlay"
          >
            <div className="absolute inset-0 bg-black/60 backdrop-blur-md" />
            <motion.div
              initial={{ opacity: 0, y: -12, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.98 }}
              transition={{ duration: 0.18, ease: [0.25, 1, 0.5, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-xl surface-glass-dark rounded-2xl overflow-hidden shadow-obsidian"
              data-testid="cmdk-panel"
            >
              {/* Search Row */}
              <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10">
                <Search className="w-4 h-4 text-obsidian-300" strokeWidth={2} />
                <input
                  ref={inputRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="輸入指令、搜尋代理或頁面…"
                  className="flex-1 bg-transparent outline-none text-white placeholder:text-obsidian-400 text-sm font-sans"
                  data-testid="cmdk-input"
                />
                <kbd className="px-1.5 py-0.5 bg-obsidian-700 rounded text-[10px] font-mono text-obsidian-200 border border-obsidian-600">
                  ESC
                </kbd>
              </div>

              {/* Results */}
              <div className="max-h-[50vh] overflow-y-auto sidebar-scroll py-2">
                {filtered.length === 0 && (
                  <div className="px-5 py-10 text-center text-obsidian-300 text-sm">
                    沒有匹配的項目
                  </div>
                )}

                {all.map((group) => {
                  const groupItems = group.items.filter((i) => filtered.includes(i));
                  if (groupItems.length === 0) return null;
                  return (
                    <div key={group._group} className="mb-2">
                      <div className="px-5 pt-3 pb-1.5 label-micro text-obsidian-400">
                        {group._group}
                      </div>
                      {groupItems.map((item) => {
                        const idx = filtered.indexOf(item);
                        const active = idx === cursor;
                        const Icon = item.icon;
                        return (
                          <button
                            key={item.id}
                            onClick={() => {
                              item.run();
                              setOpen(false);
                            }}
                            onMouseEnter={() => setCursor(idx)}
                            className={`w-full flex items-center gap-3 px-5 py-2.5 text-left transition-colors relative ${
                              active
                                ? 'bg-white/5 text-white'
                                : 'text-obsidian-100 hover:bg-white/[0.02]'
                            }`}
                            data-testid={`cmdk-item-${item.id}`}
                          >
                            {/* Neon focus bar (left border) */}
                            {active && (
                              <span
                                className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-lime-500"
                                style={{ boxShadow: '0 0 8px rgba(255,84,54,0.6)' }}
                              />
                            )}
                            <div className="w-7 h-7 rounded-md flex items-center justify-center text-base bg-white/[0.03] border border-white/10 flex-shrink-0">
                              {item.emoji || (Icon && <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate">{item.label}</div>
                              {item.description && (
                                <div className="text-xs text-obsidian-300 truncate">
                                  {item.description}
                                </div>
                              )}
                            </div>
                            {item.shortcut && (
                              <span className="label-micro text-obsidian-400 hidden sm:block">
                                {item.shortcut}
                              </span>
                            )}
                            {active && <ArrowRight className="w-3.5 h-3.5 text-lime-500" />}
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between px-5 py-2.5 border-t border-white/10 text-[10px] text-obsidian-400 label-micro">
                <div className="flex items-center gap-3">
                  <span>
                    <kbd className="px-1 py-0.5 bg-obsidian-700 rounded mr-1 font-mono text-[9px]">↑↓</kbd>
                    導覽
                  </span>
                  <span>
                    <kbd className="px-1 py-0.5 bg-obsidian-700 rounded mr-1 font-mono text-[9px]">↵</kbd>
                    選取
                  </span>
                </div>
                <span>REELSCRIPT COMMAND</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CommandPalette;
